import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Slider,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import "../../../Styles/Create/Create.css";

// ⬇️ Upload image Firebase (optionnel)
import { storage } from "../../../Services/Storage"; // ajuste le chemin si besoin
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

// ⬇️ Cropper
import Cropper from "react-easy-crop";
import getCroppedImg from "../../Admin/cropImage";

const CreateMiniBets = () => {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [sportCategory, setSportCategory] = useState("");
  const [odds, setOdds] = useState([{ label: "", value: "" }]);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  // eslint-disable-next-line
  const [bettingStatus, setBettingStatus] = useState("à venir");

  const navigate = useNavigate();
  const idOrganisation = localStorage.getItem("idOrganisation");

  // ⬇️ États image (Étape 3 - optionnelle)
  const [rawImageFile, setRawImageFile] = useState(null); // fichier sélectionné
  const [imageFile, setImageFile] = useState(null); // fichier final (croppé)
  const [imageSrc, setImageSrc] = useState(""); // objectURL pour le cropper
  const [imagePreviewURL, setImagePreviewURL] = useState(""); // preview à l’écran
  const [imageUploadURL, setImageUploadURL] = useState(""); // URL Firebase finale
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // ⬇️ Cropper states
  const [openCropper, setOpenCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropAreaPixels, setCropAreaPixels] = useState(null);

  // Étapes
  const handleNextStep = () => setStep((prev) => Math.min(prev + 1, 4));
  const handlePrevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  // Validation
  // 🔽 Ajoute ces helpers DANS le composant, juste avant canProceedToNextStep
  const trimmedTitle = title.trim();

  // normalise et compte les cotes valides (label non vide ET value > 1)
  const parsedOdds = odds.map((o) => ({
    label: (o.label ?? "").trim(),
    valueNum: parseFloat(o.value),
  }));
  const validOddsCount = parsedOdds.filter(
    (o) => o.label && !Number.isNaN(o.valueNum) && o.valueNum > 1
  ).length;

  // l'utilisateur a-t-il commencé à manipuler une image ? (sélection / preview / crop)
  const hasStartedImage = Boolean(rawImageFile || imagePreviewURL || imageSrc);

  // 🔁 Remplace ta fonction par ceci
  const canProceedToNextStep = () => {
    if (step === 1) {
      // Titre + Catégorie requis
      return Boolean(trimmedTitle && sportCategory);
    }
    if (step === 2) {
      // Min 2 cotes valides (>1) + Date
      return Boolean(date && validOddsCount >= 2);
    }
    if (step === 3) {
      // Étape image : soit aucune image n'a été commencée (passable),
      // soit l'image a été UPLOADÉE (imageUploadURL présent)
      return !hasStartedImage || Boolean(imageUploadURL);
    }
    if (step === 4) return true;
    return false;
  };

  // Gestion des cotes
  const handleAddOdd = () => setOdds([...odds, { label: "", value: "" }]);
  const handleRemoveOdd = (index) =>
    setOdds(odds.filter((_, i) => i !== index));
  const handleOddChange = (index, key, value) =>
    setOdds(
      odds.map((odd, i) => (i === index ? { ...odd, [key]: value } : odd))
    );

  // Image : sélection + ouverture du cropper
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (imagePreviewURL) URL.revokeObjectURL(imagePreviewURL);
    if (imageSrc) URL.revokeObjectURL(imageSrc);

    setRawImageFile(file);
    setImageFile(null);
    setImageUploadURL("");
    setUploadProgress(0);

    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setOpenCropper(true);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setAlertMessage("");
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCropAreaPixels(croppedPixels);
  }, []);

  const handleCropCancel = () => {
    // si annule, on utilise l’original pour la preview
    if (imagePreviewURL) URL.revokeObjectURL(imagePreviewURL);
    const preview = imageSrc || "";
    setImagePreviewURL(preview);
    setOpenCropper(false);
  };

  const handleCropConfirm = async () => {
    if (!imageSrc || !cropAreaPixels) return;
    const croppedBlob = await getCroppedImg(imageSrc, cropAreaPixels);

    const safeTitle = (title || "minibet")
      .replace(/[^a-z0-9-_]+/gi, "_")
      .toLowerCase();
    const finalFile = new File(
      [croppedBlob],
      `${Date.now()}_${safeTitle}.jpg`,
      {
        type: "image/jpeg",
      }
    );

    if (imagePreviewURL) URL.revokeObjectURL(imagePreviewURL);
    const previewURL = URL.createObjectURL(finalFile);
    setImageFile(finalFile);
    setImagePreviewURL(previewURL);
    setOpenCropper(false);
  };

  // Upload vers Firebase Storage (croppé si dispo, sinon brut)
  const handleUploadImage = () => {
    const fileToUpload = imageFile || rawImageFile;
    if (!fileToUpload) {
      setAlertMessage("Sélectionnez (et recadrez) une image avant l’upload.");
      setAlertSeverity("error");
      return;
    }

    const ext =
      fileToUpload.name.split(".").pop()?.toLowerCase() ||
      (fileToUpload.type.includes("png") ? "png" : "jpg");
    const safeTitle = (title || "minibet")
      .replace(/[^a-z0-9-_]+/gi, "_")
      .toLowerCase();
    const fileName = `${Date.now()}_${safeTitle}.${ext}`;

    const storageRef = ref(
      storage,
      `minibet-images/${idOrganisation || "org"}/${fileName}`
    );
    const uploadTask = uploadBytesResumable(storageRef, fileToUpload, {
      contentType: fileToUpload.type,
    });

    setUploadingImage(true);
    setAlertMessage("");

    uploadTask.on(
      "state_changed",
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setUploadProgress(pct);
      },
      (error) => {
        console.error(error);
        setUploadingImage(false);
        setAlertMessage("Erreur lors de l’upload de l’image.");
        setAlertSeverity("error");
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setImageUploadURL(url);
        setUploadingImage(false);
        setAlertMessage("Image téléchargée avec succès !");
        setAlertSeverity("success");
      }
    );
  };

  // Soumission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const betsData = {
      path: `organisations/${idOrganisation}/miniBets`,
      data: {
        bettingOpen: true,
        bettingStatus,
        date,
        odds,
        sportCategory,
        title,
        distributeWinningDone: false,
        imageMiniBetUrl: imageUploadURL || "",
      },
    };

    try {
      const response = await fetch(
        "https://backend-betkiff.vercel.app/bets/addBets",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(betsData),
        }
      );
      const result = await response.text();
      setAlertMessage(result);
      setAlertSeverity("success");
      navigate("/");
    } catch (error) {
      setAlertMessage("Erreur lors de la création du mini pari.");
      setAlertSeverity("error");
    }
  };

  // cleanup objectURLs
  useEffect(() => {
    return () => {
      if (imagePreviewURL) URL.revokeObjectURL(imagePreviewURL);
      if (imageSrc) URL.revokeObjectURL(imageSrc);
    };
  }, [imagePreviewURL, imageSrc]);

  const progressValue = (step / 4) * 100;

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
      <Container maxWidth="sm" className="create-container">
        {/* En-tête progression */}
        <Box
          sx={{
            position: "absolute",
            top: "0",
            left: "0",
            width: "100%",
            display: "flex",
            alignItems: "center",
            backgroundColor: "white",
            padding: "2vh",
            boxSizing: "border-box",
          }}>
          <Box
            sx={{
              position: "relative",
              width: "70px",
              height: "70px",
              marginRight: "10px",
            }}>
            <CircularProgress
              variant="determinate"
              value={100}
              size={70}
              thickness={5}
              sx={{ color: "rgba(90, 20, 121, 0.3)", position: "absolute" }}
            />
            <CircularProgress
              variant="determinate"
              value={progressValue}
              size={70}
              thickness={5}
              sx={{ color: "#5A147E" }}
            />
            <Typography
              sx={{
                position: "absolute",
                fontSize: "16px",
                fontWeight: "bold",
                color: "#5A147E",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}>
              {step}/4
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: "bold",
                color: "rgba(90, 20, 121, 254)",
                pl: "2vw",
              }}>
              {step === 1 && "Étape 1"}
              {step === 2 && "Étape 2"}
              {step === 3 && "Étape 3"}
              {step === 4 && "Étape 4"}
            </Typography>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: "bold",
                color: "rgba(90, 20, 121, 254)",
                pl: "2vw",
                fontSize: "1rem",
              }}>
              {step === 1 && "Informations Générales"}
              {step === 2 && "Date & Cotes"}
              {step === 3 && "Image (optionnelle) + Recadrage"}
              {step === 4 && "Vérification et Création"}
            </Typography>
          </Box>
        </Box>

        {alertMessage && (
          <Alert severity={alertSeverity} className="create-alert">
            {alertMessage}
          </Alert>
        )}

        {/* Étape 1 */}
        {step === 1 && (
          <form className="create-form">
            <TextField
              label="Titre"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ marginBottom: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel>Catégorie du Sport</InputLabel>
              <Select
                value={sportCategory}
                onChange={(e) => setSportCategory(e.target.value)}
                label="Catégorie du Sport">
                <MenuItem value="bde">BDE</MenuItem>
                <MenuItem value="bds">BDS</MenuItem>
                <MenuItem value="bda">BDA</MenuItem>
                <MenuItem value="bdf">BDF</MenuItem>
                <MenuItem value="event">Evenement / soirée</MenuItem>
                <MenuItem value="football">Football</MenuItem>
                <MenuItem value="basketball">Basketball</MenuItem>
                <MenuItem value="rugby">Rugby</MenuItem>
                <MenuItem value="spikeball">Spikeball</MenuItem>
                <MenuItem value="handball">Handball</MenuItem>
                <MenuItem value="volley">Volley</MenuItem>
                <MenuItem value="autre">Autre</MenuItem>
              </Select>
            </FormControl>
          </form>
        )}

        {/* Étape 2 */}
        {step === 2 && (
          <form className="create-form">
            <Grid item xs={12}>
              <TextField
                label="Date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                variant="outlined"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            {odds.map((odd, index) => (
              <Grid container spacing={1} key={index} alignItems="center">
                <Grid item xs={5}>
                  <TextField
                    label="Titre Côte"
                    value={odd.label}
                    onChange={(e) =>
                      handleOddChange(index, "label", e.target.value)
                    }
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    label="Valeur"
                    type="number"
                    value={odd.value}
                    onChange={(e) =>
                      handleOddChange(index, "value", e.target.value)
                    }
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    onClick={() => handleRemoveOdd(index)}
                    color="error">
                    <Remove />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Button
              variant="outlined"
              color="primary"
              startIcon={<Add />}
              onClick={handleAddOdd}
              sx={{ mt: 2 }}>
              Ajouter une Côte
            </Button>
          </form>
        )}

        {/* Sous l'étape 2 */}
        {step === 2 && !(date && validOddsCount >= 2) && (
          <Typography
            variant="caption"
            color="black"
            sx={{ mt: 1, display: "block" }}>
            Il faut une date et au moins 2 cotes valides (valeur &gt; 1).
          </Typography>
        )}

        {/* Étape 3 — Image (optionnelle & passable) */}
        {step === 3 && (
          <form className="create-form">
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Image du mini pari (optionnelle)
            </Typography>

            <Box
              sx={{
                width: "100%",
                height: 200,
                borderRadius: "10px",
                border: "2px dashed #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                mb: 1.5,
              }}>
              {imagePreviewURL || imageUploadURL ? (
                <img
                  src={imageUploadURL || imagePreviewURL}
                  alt="Aperçu"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Typography variant="body2" color="gray">
                  Aperçu de l’image
                </Typography>
              )}
            </Box>

            {!imageUploadURL && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}>
                {!rawImageFile && (
                  <>
                    <Button variant="outlined" component="label">
                      Sélectionner une image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageSelect}
                      />
                    </Button>
                    <Button
                      onClick={handleNextStep}
                      variant="text"
                      sx={{ flexGrow: 1 }}>
                      Passer
                    </Button>
                  </>
                )}

                {rawImageFile && (
                  <>
                    <Button variant="text" onClick={() => setOpenCropper(true)}>
                      Recadrer à nouveau
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleUploadImage}
                      disabled={!(imageFile || rawImageFile) || uploadingImage}>
                      {uploadingImage ? "Upload en cours…" : "Uploader l’image"}
                    </Button>
                  </>
                )}
              </Box>
            )}

            {uploadingImage && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption">{uploadProgress}%</Typography>
              </Box>
            )}

            {imageUploadURL && (
              <Typography variant="body2" sx={{ mt: 1 }} color="success.main">
                Image prête ✅
              </Typography>
            )}
          </form>
        )}
        {/* Sous l'étape 3 */}
        {step === 3 && hasStartedImage && !imageUploadURL && (
          <Typography
            variant="caption"
            color="black"
            sx={{ mt: 1, display: "block" }}>
            Vous avez sélectionné une image : uploadez-la pour continuer.
          </Typography>
        )}

        {/* Étape 4 — Preview & Création */}
        {step === 4 && (
          <Card
            sx={{
              maxWidth: 400,
              borderRadius: 5,
              overflow: "hidden",
              boxShadow: 3,
              backgroundColor: "#F8F0FC",
            }}>
            <Box
              sx={{
                backgroundColor: "#6A1B9A",
                padding: "10px",
                textAlign: "center",
                color: "white",
              }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {title}
              </Typography>
              <Typography variant="body2">
                {new Date(date).toLocaleDateString("fr-FR", {
                  weekday: "short",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </Typography>
            </Box>

            {/* Image si dispo */}
            {imageUploadURL && (
              <img
                src={imageUploadURL}
                alt={`Mini pari - ${title}`}
                style={{ width: "100%", height: 150, objectFit: "cover" }}
              />
            )}

            <CardContent sx={{ textAlign: "center" }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", color: "#4A148C", mb: 1 }}>
                {sportCategory}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {odds.map((odd, index) => (
                  <Grid item xs={4} key={index}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: "#E1BEE7",
                        color: "#4A148C",
                        fontWeight: "bold",
                      }}>
                      {odd.label}
                      <br />
                      {odd.value}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Boutons de navigation */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            display: "flex",
            padding: "2vh",
            width: "100%",
            boxSizing: "border-box",
            backgroundColor: "white",
            gap: 2,
          }}>
          {step === 1 && (
            <Button
              onClick={() => navigate("/dashboard")}
              variant="outlined"
              sx={{
                flexGrow: 1,
                color: "rgba(90, 20, 121, 254)",
                fontWeight: "bold",
                border: "1px solid rgba(90, 20, 121, 254)",
              }}>
              Dashboard
            </Button>
          )}
          {step > 1 && (
            <Button
              onClick={handlePrevStep}
              variant="outlined"
              sx={{
                flexGrow: 1,
                color: "rgba(90, 20, 121, 254)",
                fontWeight: "bold",
                border: "1px solid rgba(90, 20, 121, 254)",
              }}>
              Retour
            </Button>
          )}

          {step < 4 ? (
            <Button
              onClick={handleNextStep}
              variant="contained"
              sx={{
                flexGrow: 1,
                backgroundColor: "rgba(90, 20, 121, 254)",
                fontWeight: "bold",
                color: "white",
              }}
              disabled={!canProceedToNextStep()}>
              Suivant
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                flexGrow: 1,
                backgroundColor: "rgba(90, 20, 121, 254)",
                fontWeight: "bold",
                color: "white",
              }}>
              Créer le Mini Pari
            </Button>
          )}
        </Box>
      </Container>

      {/* 🔳 Dialog de recadrage */}
      <Dialog
        open={openCropper}
        onClose={handleCropCancel}
        fullWidth
        maxWidth="sm"
        sx={{ "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.8)" } }}>
        <DialogContent sx={{ backgroundColor: "#222", color: "#fff" }}>
          <Typography variant="h6" textAlign="center" sx={{ mb: 2 }}>
            Recadrez votre image
          </Typography>
          <Box sx={{ position: "relative", width: "100%", height: 400 }}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              // ⬇️ Ajuste le ratio à la zone d'affichage de MiniBetCard
              aspect={16 / 9} // ton MiniBet affiche ~100% x 150px (cover) → 16/9 très proche
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition={true}
              showGrid={false}
            />
          </Box>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(_, v) => setZoom(v)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCropCancel} color="secondary">
            Annuler
          </Button>
          <Button
            onClick={handleCropConfirm}
            variant="contained"
            color="primary">
            Valider le recadrage
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateMiniBets;
